/**
 * Typography Token System
 *
 * Defines all typography-related design tokens for the EroChat application.
 * These tokens are compatible with the existing Typography interface from
 * `src/lib/api/design.ts` and extend it with additional semantic aliases.
 *
 * @module design-system/tokens/typography
 */

// Import TypographyTokens from types.ts for consistent interface
import type { TypographyTokens } from './types';

// ============================================================================
// Font Family Tokens
// ============================================================================

/**
 * Font family configuration for the design system.
 * Uses the established font stack: Poppins (primary), Inter (secondary), Montserrat (accent).
 */
export interface FontFamilyTokens {
  /** Primary font for headings and important text */
  primary: string;
  /** Secondary font for body text and UI elements */
  secondary: string;
  /** Accent font for special elements and decorative text */
  accent: string;
  /** Monospace font for code */
  mono: string;
}

/**
 * Default font families matching the existing design system.
 */
export const fontFamilies: FontFamilyTokens = {
  primary: "'Poppins', sans-serif",
  secondary: "'Inter', sans-serif",
  accent: "'Montserrat', sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
} as const;

// ============================================================================
// Font Size Tokens
// ============================================================================

/**
 * Font size scale using rem units for accessibility.
 * Based on a modular scale with 1rem = 16px as the base.
 */
export interface FontSizeTokens {
  /** Extra small: 12px / 0.75rem */
  xs: string;
  /** Small: 14px / 0.875rem */
  sm: string;
  /** Base: 16px / 1rem */
  base: string;
  /** Large: 18px / 1.125rem */
  lg: string;
  /** Extra large: 20px / 1.25rem */
  xl: string;
  /** 2x large: 24px / 1.5rem */
  '2xl': string;
  /** 3x large: 30px / 1.875rem */
  '3xl': string;
  /** 4x large: 36px / 2.25rem */
  '4xl': string;
  /** 5x large: 48px / 3rem */
  '5xl': string;
  /** 6x large: 64px / 4rem */
  '6xl': string;
}

/**
 * Default font sizes using rem units.
 */
export const fontSizes: FontSizeTokens = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '4rem',
} as const;

// ============================================================================
// Font Weight Tokens
// ============================================================================

/**
 * Font weight scale following standard CSS font-weight values.
 */
export interface FontWeightTokens {
  /** Light: 300 */
  light: number;
  /** Normal: 400 */
  normal: number;
  /** Medium: 500 */
  medium: number;
  /** Semibold: 600 */
  semibold: number;
  /** Bold: 700 */
  bold: number;
}

/**
 * Default font weights.
 */
export const fontWeights: FontWeightTokens = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// ============================================================================
// Line Height Tokens
// ============================================================================

/**
 * Line height scale for different text densities.
 */
export interface LineHeightTokens {
  /** None: 1 (for single-line text) */
  none: number;
  /** Tight: 1.25 (for headings) */
  tight: number;
  /** Snug: 1.375 (slightly tight) */
  snug: number;
  /** Normal: 1.5 (for body text) */
  normal: number;
  /** Relaxed: 1.75 (for comfortable reading) */
  relaxed: number;
  /** Loose: 2 (for very spacious text) */
  loose: number;
}

/**
 * Default line heights.
 */
export const lineHeights: LineHeightTokens = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.75,
  loose: 2,
} as const;

// ============================================================================
// Letter Spacing Tokens
// ============================================================================

/**
 * Letter spacing scale for fine typography adjustments.
 */
export interface LetterSpacingTokens {
  /** Tighter: -0.05em */
  tighter: string;
  /** Tight: -0.025em */
  tight: string;
  /** Normal: 0 */
  normal: string;
  /** Wide: 0.025em */
  wide: string;
  /** Wider: 0.05em */
  wider: string;
  /** Widest: 0.1em */
  widest: string;
}

/**
 * Default letter spacing values.
 */
export const letterSpacings: LetterSpacingTokens = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
} as const;

// ============================================================================
// Semantic Typography Aliases
// ============================================================================

/**
 * Semantic typography configuration for specific use cases.
 * Maps semantic names to combinations of size, weight, line height, and font family.
 */
export interface SemanticTypographyToken {
  /** Font family reference */
  fontFamily: keyof FontFamilyTokens;
  /** Font size reference */
  fontSize: keyof FontSizeTokens;
  /** Font weight reference */
  fontWeight: keyof FontWeightTokens;
  /** Line height reference */
  lineHeight: keyof LineHeightTokens;
  /** Optional letter spacing reference */
  letterSpacing?: keyof LetterSpacingTokens;
}

/**
 * Semantic typography aliases for common text styles.
 */
export interface SemanticTypographyTokens {
  /** Hero title: Large, bold primary font for landing pages */
  heroTitle: SemanticTypographyToken;
  /** Page title: Main page heading */
  pageTitle: SemanticTypographyToken;
  /** Section title: Section headings within pages */
  sectionTitle: SemanticTypographyToken;
  /** Subsection title: Smaller section headings */
  subsectionTitle: SemanticTypographyToken;
  /** Card title: Title text for cards */
  cardTitle: SemanticTypographyToken;
  /** Body: Standard body text */
  body: SemanticTypographyToken;
  /** Body large: Larger body text for emphasis */
  bodyLarge: SemanticTypographyToken;
  /** Small: Small text for captions and metadata */
  small: SemanticTypographyToken;
  /** Tiny: Extra small text for fine print */
  tiny: SemanticTypographyToken;
  /** Button: Text style for buttons */
  button: SemanticTypographyToken;
  /** Label: Text style for form labels */
  label: SemanticTypographyToken;
  /** Caption: Text style for image captions and descriptions */
  caption: SemanticTypographyToken;
  /** Code: Monospace text for code snippets */
  code: SemanticTypographyToken;
}

/**
 * Default semantic typography configurations.
 */
export const semanticTypography: SemanticTypographyTokens = {
  heroTitle: {
    fontFamily: 'primary',
    fontSize: '6xl',
    fontWeight: 'bold',
    lineHeight: 'tight',
    letterSpacing: 'tight',
  },
  pageTitle: {
    fontFamily: 'primary',
    fontSize: '4xl',
    fontWeight: 'bold',
    lineHeight: 'tight',
  },
  sectionTitle: {
    fontFamily: 'primary',
    fontSize: '2xl',
    fontWeight: 'bold',
    lineHeight: 'tight',
  },
  subsectionTitle: {
    fontFamily: 'primary',
    fontSize: 'xl',
    fontWeight: 'semibold',
    lineHeight: 'snug',
  },
  cardTitle: {
    fontFamily: 'primary',
    fontSize: 'lg',
    fontWeight: 'semibold',
    lineHeight: 'snug',
  },
  body: {
    fontFamily: 'secondary',
    fontSize: 'base',
    fontWeight: 'normal',
    lineHeight: 'normal',
  },
  bodyLarge: {
    fontFamily: 'secondary',
    fontSize: 'lg',
    fontWeight: 'normal',
    lineHeight: 'relaxed',
  },
  small: {
    fontFamily: 'secondary',
    fontSize: 'sm',
    fontWeight: 'normal',
    lineHeight: 'normal',
  },
  tiny: {
    fontFamily: 'secondary',
    fontSize: 'xs',
    fontWeight: 'normal',
    lineHeight: 'normal',
  },
  button: {
    fontFamily: 'secondary',
    fontSize: 'sm',
    fontWeight: 'semibold',
    lineHeight: 'none',
  },
  label: {
    fontFamily: 'secondary',
    fontSize: 'sm',
    fontWeight: 'medium',
    lineHeight: 'none',
  },
  caption: {
    fontFamily: 'secondary',
    fontSize: 'xs',
    fontWeight: 'normal',
    lineHeight: 'normal',
  },
  code: {
    fontFamily: 'secondary', // Will be overridden with monospace in CSS
    fontSize: 'sm',
    fontWeight: 'normal',
    lineHeight: 'normal',
  },
} as const;

// ============================================================================
// Complete Typography Tokens Interface
// ============================================================================

/**
 * Default typography tokens combining all typography-related values.
 * Uses singular property names to match TypographyTokens interface from types.ts.
 */
export const defaultTypographyTokens: TypographyTokens = {
  fontFamily: fontFamilies,
  fontSize: fontSizes,
  fontWeight: fontWeights,
  lineHeight: lineHeights,
  letterSpacing: letterSpacings,
  semantic: semanticTypography,
};

// ============================================================================
// Legacy API Compatibility
// ============================================================================

/**
 * Creates a Typography object compatible with the existing design API.
 * Maps the new token system to the legacy Typography interface from `src/lib/api/design.ts`.
 *
 * @param tokens - Optional typography tokens to convert (defaults to defaultTypographyTokens)
 * @returns Typography object compatible with the design API
 */
export function createLegacyTypography(tokens: TypographyTokens = defaultTypographyTokens): {
  primary_font: string;
  secondary_font: string;
  accent_font: string;
  hero_title_size: number;
  page_title_size: number;
  section_title_size: number;
  body_text_size: number;
  small_text_size: number;
  title_weight: number;
  body_weight: number;
} {
  /**
   * Converts rem string to numeric pixel value.
   * Assumes 1rem = 16px base font size.
   */
  const remToPixels = (rem: string): number => {
    const value = parseFloat(rem.replace('rem', ''));
    return value * 16;
  };

  return {
    primary_font: tokens.fontFamily.primary,
    secondary_font: tokens.fontFamily.secondary,
    accent_font: tokens.fontFamily.accent,
    hero_title_size: remToPixels(tokens.fontSize['6xl']),
    page_title_size: remToPixels(tokens.fontSize['4xl']),
    section_title_size: remToPixels(tokens.fontSize['2xl']),
    body_text_size: remToPixels(tokens.fontSize.base),
    small_text_size: remToPixels(tokens.fontSize.sm),
    title_weight: tokens.fontWeight.bold,
    body_weight: tokens.fontWeight.normal,
  };
}

// ============================================================================
// CSS Custom Properties Generation
// ============================================================================

/**
 * Generates CSS custom properties from typography tokens.
 * These can be injected into the DOM for runtime theming.
 *
 * @param tokens - Typography tokens to convert to CSS custom properties
 * @returns Record of CSS variable names to values
 */
export function generateTypographyCSSVariables(
  tokens: TypographyTokens = defaultTypographyTokens
): Record<string, string> {
  return {
    // Font families
    '--font-primary': tokens.fontFamily.primary,
    '--font-secondary': tokens.fontFamily.secondary,
    '--font-accent': tokens.fontFamily.accent,
    '--font-mono': tokens.fontFamily.mono,

    // Font sizes
    '--font-size-xs': tokens.fontSize.xs,
    '--font-size-sm': tokens.fontSize.sm,
    '--font-size-base': tokens.fontSize.base,
    '--font-size-lg': tokens.fontSize.lg,
    '--font-size-xl': tokens.fontSize.xl,
    '--font-size-2xl': tokens.fontSize['2xl'],
    '--font-size-3xl': tokens.fontSize['3xl'],
    '--font-size-4xl': tokens.fontSize['4xl'],
    '--font-size-5xl': tokens.fontSize['5xl'],
    '--font-size-6xl': tokens.fontSize['6xl'],

    // Legacy size aliases (compatible with existing CSS)
    '--font-size-hero': tokens.fontSize['6xl'],
    '--font-size-page-title': tokens.fontSize['4xl'],
    '--font-size-section-title': tokens.fontSize['2xl'],
    '--font-size-body': tokens.fontSize.base,
    '--font-size-small': tokens.fontSize.sm,

    // Font weights
    '--font-weight-light': String(tokens.fontWeight.light),
    '--font-weight-normal': String(tokens.fontWeight.normal),
    '--font-weight-medium': String(tokens.fontWeight.medium),
    '--font-weight-semibold': String(tokens.fontWeight.semibold),
    '--font-weight-bold': String(tokens.fontWeight.bold),

    // Legacy weight aliases
    '--font-weight-title': String(tokens.fontWeight.bold),
    '--font-weight-body': String(tokens.fontWeight.normal),

    // Line heights
    '--line-height-none': String(tokens.lineHeight.none),
    '--line-height-tight': String(tokens.lineHeight.tight),
    '--line-height-snug': String(tokens.lineHeight.snug),
    '--line-height-normal': String(tokens.lineHeight.normal),
    '--line-height-relaxed': String(tokens.lineHeight.relaxed),
    '--line-height-loose': String(tokens.lineHeight.loose),

    // Letter spacings
    '--letter-spacing-tighter': tokens.letterSpacing.tighter,
    '--letter-spacing-tight': tokens.letterSpacing.tight,
    '--letter-spacing-normal': tokens.letterSpacing.normal,
    '--letter-spacing-wide': tokens.letterSpacing.wide,
    '--letter-spacing-wider': tokens.letterSpacing.wider,
    '--letter-spacing-widest': tokens.letterSpacing.widest,
  };
}

// ============================================================================
// Exports
// ============================================================================

export {
  fontFamilies as defaultFontFamilies,
  fontSizes as defaultFontSizes,
  fontWeights as defaultFontWeights,
  lineHeights as defaultLineHeights,
  letterSpacings as defaultLetterSpacings,
  semanticTypography as defaultSemanticTypography,
};
